import { InjectionToken } from '@angular/core';
import { Observable } from 'rxjs';
import {
  EffectiveStorageFeeSettings,
  StorageFeeInvoiceListItem,
  StorageFeeListFilter,
  StorageFeeOverridesDto,
  StorageFeeSettings,
  StorageFeeSettingsUpdateDto,
} from '@models/resources/storage-fee.model';

/**
 * Admin-Lagergebühren (Sprint 3, S3-9). Bündelt:
 *
 *  - Globale Defaults (Singleton, §2.2.11)
 *  - Pro-Kunden-Overrides (§2.2.2)
 *  - Effektive Werte für ein Kunden-Detail (auflösung override → default)
 *  - Gesamtliste der erzeugten Rechnungen mit Filtern (§7.4 Admin)
 *  - Customer-Liste (Detail-Panel-Historie)
 *  - Mark-Paid + Mark-Unpaid (ADR-003 D4) inkl. Audit-Log
 *  - Bulk-Mark-Paid (Konzept §7.4 Sammelaktion, V3 §5.7)
 *  - Download / Resend (Stubs in S3-9, gegen Sprint 5/6 BE)
 *
 * Mapping auf BE in Sprint 5/6:
 *  - getSettings/saveSettings        → /api/storage-fees/settings  (GET/PATCH)
 *  - getOverrides/saveOverrides      → /api/customers/{id}/storage-fee-overrides
 *  - list / listForCustomer          → /api/storage-fee-invoices
 *  - markPaid / markUnpaid           → /api/storage-fee-invoices/{id}/mark-paid
 *  - markPaidBulk                    → /api/storage-fee-invoices/mark-paid-bulk
 *  - download / resend               → /api/invoices/{id}/download bzw. /resend
 *
 * Effektive Werte werden im Dummy clientseitig aufgelöst — im
 * HTTP-Modus liefert das BE direkt eine `EffectiveStorageFeeSettings`
 * im Customer-Resource (alternativ über einen eigenen Endpoint).
 */
export interface AdminStorageFeesFacade {
  // --- Globale Defaults ----------------------------------------------
  getSettings(): Observable<StorageFeeSettings>;
  saveSettings(dto: StorageFeeSettingsUpdateDto): Observable<StorageFeeSettings>;

  // --- Pro-Kunden-Overrides ------------------------------------------
  getOverrides(customerId: number): Observable<StorageFeeOverridesDto>;
  saveOverrides(
    customerId: number,
    dto: StorageFeeOverridesDto,
  ): Observable<StorageFeeOverridesDto>;
  /** Aufgelöste Werte mit Source-Hinweis. */
  getEffective(customerId: number): Observable<EffectiveStorageFeeSettings>;

  // --- Rechnungen ----------------------------------------------------
  list(filter: StorageFeeListFilter): Observable<StorageFeeInvoiceListItem[]>;
  listForCustomer(customerId: number): Observable<StorageFeeInvoiceListItem[]>;

  markPaid(id: number): Observable<StorageFeeInvoiceListItem>;
  /** ADR-003 D4: paid_at darf zurückgenommen werden, mit Audit-Log. */
  markUnpaid(id: number): Observable<StorageFeeInvoiceListItem>;
  markPaidBulk(ids: readonly number[]): Observable<StorageFeeInvoiceListItem[]>;

  download(id: number): Observable<Blob>;
  resend(id: number): Observable<void>;
}

export const ADMIN_STORAGE_FEES_FACADE = new InjectionToken<AdminStorageFeesFacade>(
  'AdminStorageFeesFacade',
);
